import React, { useCallback, useMemo, useState } from "react";
import { toast } from "react-toastify";
import { Button } from "@commonsku/styles";
import { BulkActionConfigBase } from "../index";
import { useIdentity } from "../../../../hooks";
import { oauth, fetchCsv, createDownload } from "../../../../utils";
import { useReportContext } from "../../report_context";
import { createExportToAccountingButton } from "../common/bulk-actions";

export function prepareLabel<
  T extends Record<string, unknown> = Record<string, unknown>
>(selected: T[]): React.ReactNode {
  if (selected.length === 0) {
    return "Select one or more invoices";
  }
  if (selected.length === 1) {
    return "1 invoice selected";
  }
  return `${selected.length} invoices selected`;
}

const ExportToAccountingButton = createExportToAccountingButton(
  "INVOICE",
  (row: any) => row.invoice_number.order_id,
  true
);

export function UnExportFromAccountingButton<T extends Record<string, unknown> = Record<string, unknown>>(
  {
    selected,
    setExecuting,
    disabled,
  }: {
    selected: T[],
    setExecuting: (executing?: boolean) => void,
    disabled?: boolean,
  }
) {
  const { fetchReport } = useReportContext();
  const order_ids = useMemo(
    () => selected.map((row: any) => row.invoice_number.order_id),
    [selected]
  );

  const handleClick = useCallback(async () => {
    if (order_ids.length === 1) {
      if (!confirm("Are you sure to want to un-export this invoice?")) {
        return;
      }
    } else {
      if (!confirm("Are you sure to want to un-export these invoices?")) {
        return;
      }
    }
    setExecuting(true);
    try {
      await oauth("POST", "order", { action: "unexport", ids: order_ids });
      if (order_ids.length === 1) {
        toast.success("1 invoice unexported");
      } else {
        toast.success(`${order_ids.length} invoices unexported`);
      }
      await fetchReport();
    } catch (e) {
      toast.error("Unable to un-export invoices");
    } finally {
      setExecuting(false);
    }
  }, [setExecuting, order_ids]);

  return (
    <Button
      disabled={disabled}
      onClick={handleClick}
    >
      Un-Export
    </Button>
  );
}

export const BulkActions: BulkActionConfigBase = {
  prepareLabel,
  actions: [
    ExportToAccountingButton,
    UnExportFromAccountingButton,
  ]
};
